import React, { useState, useMemo } from 'react';
import { Box, Typography, Link as MuiLink } from '@mui/material';
import { Email as EmailIcon, Phone as PhoneIcon } from '@mui/icons-material';
import { detectFields } from './detectFields';

function FieldRow({ label, value, isLong }) {
  return (
    <Box sx={{
      gridColumn: isLong ? '1 / -1' : 'auto',
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
    }}>
      <Typography component="div" sx={{
        fontSize: '10px',
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        color: 'text.secondary',
        fontWeight: 600,
      }}>
        {label}
      </Typography>
      <Typography
        component="div"
        sx={{
          fontSize: '13px',
          color: 'text.primary',
          wordBreak: 'break-word',
          ...(isLong && {
            backgroundColor: 'grey.50',
            padding: '8px 10px',
            borderRadius: '4px',
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
          }),
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function SubmissionCard({ submission, formType }) {
  const [expanded, setExpanded] = useState(false);
  const detected = useMemo(() => detectFields(submission, formType), [submission, formType]);
  const { entryId, name, email, phone, message, date, rest } = detected;

  const totalFieldCount =
    (name ? 1 : 0) +
    (email ? 1 : 0) +
    (phone ? 1 : 0) +
    (message ? 1 : 0) +
    rest.length;

  const messageIsLong = message && (message.includes('\n') || message.length > 100);
  const showExpandLink = rest.length > 0 || messageIsLong;

  return (
    <Box sx={{
      padding: '14px 16px',
      mb: 1.5,
      backgroundColor: 'background.paper',
      border: '1px solid',
      borderColor: 'grey.200',
      borderRadius: '8px',
      boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
      '&:last-child': { mb: 0 },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
        <Typography component="div" sx={{ fontWeight: 600, fontSize: '15px', color: 'text.primary' }}>
          {name || (entryId ? `Entry #${entryId}` : 'Submission')}
        </Typography>
        {date && (
          <Typography component="div" sx={{ color: 'text.secondary', fontSize: '12px' }}>
            {date.label}
          </Typography>
        )}
      </Box>

      {(email || phone) && (
        <Box sx={{ display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '13px', color: 'text.primary', mb: 1 }}>
          {email && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <EmailIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
              <MuiLink href={`mailto:${email}`} sx={{ color: 'primary.main' }}>{email}</MuiLink>
            </Box>
          )}
          {phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <PhoneIcon sx={{ fontSize: '14px', color: 'text.secondary' }} />
              <span>{phone}</span>
            </Box>
          )}
        </Box>
      )}

      {message && (
        <Box sx={{
          backgroundColor: 'grey.50',
          padding: '8px 12px',
          borderRadius: '6px',
          borderLeft: '3px solid',
          borderColor: 'grey.300',
          color: 'text.primary',
          fontSize: '13px',
          lineHeight: 1.55,
          display: '-webkit-box',
          WebkitLineClamp: expanded ? 'unset' : 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          whiteSpace: 'pre-wrap',
        }}>
          {message}
        </Box>
      )}

      {expanded && (rest.length > 0 || messageIsLong) && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '12px 24px',
          mt: 1.5,
          pt: 1.5,
          borderTop: '1px dashed',
          borderColor: 'grey.200',
        }}>
          {rest.map(f => (
            <FieldRow key={f.key} label={f.label} value={f.value} isLong={f.isLong} />
          ))}
          {messageIsLong && (
            <FieldRow label="Full Message" value={message} isLong={true} />
          )}
        </Box>
      )}

      {showExpandLink && (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setExpanded(e => !e)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded(x => !x); } }}
          aria-expanded={expanded}
          aria-label={expanded ? 'Hide fields' : `Show all ${totalFieldCount} fields`}
          sx={{
            mt: 1,
            color: 'primary.main',
            fontSize: '12px',
            cursor: 'pointer',
            display: 'inline-block',
            userSelect: 'none',
          }}
        >
          {expanded ? '▾ Hide fields' : `▸ Show all ${totalFieldCount} fields`}
        </Box>
      )}
    </Box>
  );
}
